import {TransferType} from '@transactions/graphql';
import {ChevronDown} from 'lucide-react';
import {cn} from 'src/lib/utils';

export const TRANSFER_TYPE_LABELS: Record<TransferType, string> = {
  [TransferType.CreditCardPayment]: 'Credit Card Payment',
  [TransferType.AccountTransfer]: 'Account Transfer',
  [TransferType.Prepaid]: 'Prepaid Reload',
  [TransferType.Refund]: 'Refund',
  [TransferType.Reimbursement]: 'Reimbursement',
  [TransferType.Conversion]: 'Currency Conversion',
};

// Conversion is deliberately not offered here: a conversion can only be
// created from a two-currency raw selection, where CreateTransactionModal
// locks the type to it. Single-currency transfers can never become one.
const TRANSFER_TYPE_OPTIONS: TransferType[] = [
  TransferType.CreditCardPayment,
  TransferType.AccountTransfer,
  TransferType.Prepaid,
  TransferType.Refund,
  TransferType.Reimbursement,
];

const isTransferType = (value: string): value is TransferType => {
  return Object.keys(TRANSFER_TYPE_LABELS).includes(value);
};

type Props = {
  className?: string;
  value: TransferType | null;
  onChange: (value: TransferType) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const TransferTypeSelect = (props: Props) => {
  const {className, value, onChange, placeholder = 'Select type…'} = props;
  // A conversion's type is immutable (server-enforced); when one is shown,
  // it is the only option and the control is locked.
  const disabled = props.disabled || value === TransferType.Conversion;
  const options =
    value === TransferType.Conversion
      ? [TransferType.Conversion]
      : TRANSFER_TYPE_OPTIONS;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    if (isTransferType(newValue)) {
      onChange(newValue);
    }
  };

  return (
    <span
      className={cn(
        'has-focus:border-ring border-input has-focus:ring-ring/50 relative flex flex-row items-center gap-1 rounded-md border px-2 py-1 shadow-xs has-focus:ring-[3px]',
        className,
      )}
    >
      <span className={cn(value === null && 'text-muted-foreground')}>
        {value === null ? placeholder : TRANSFER_TYPE_LABELS[value]}
      </span>{' '}
      <ChevronDown className="size-4" />
      <select
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled}
      >
        {value === null && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option} value={option}>
            {TRANSFER_TYPE_LABELS[option]}
          </option>
        ))}
      </select>
    </span>
  );
};
